import BottomBar from './BottomBar';

import React, { useEffect, useMemo, useState } from 'react';
import { calculateTotals, getFormActionButtons, getFormTitle, transformFormData } from '../../utils';
import { formSchema, FormSchema, initialFormValues } from '../../schema';
import { handleUpdateRowFields } from '../../utils/calc-util';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import { AritoFormV2 } from '@/components/custom/erp';
import { useAuth } from '@/contexts/auth-context';
import { useFormFieldState } from '../../hooks';
import { HoverDropdown } from './Dropdown';
import BasicInfoTab from './BasicInfoTab';
import EInvoiceTab from './EInvoiceTab';
import { FormMode } from '@/types/form';
import { useDetailRows } from './hooks';
import HistoryTab from './HistoryTab';

import DetailTab from './DetailTab';
import OtherTab from './OtherTab';
import TaxTab from './TaxTab';

interface FormDialogProps {
  formMode: FormMode;
  open?: boolean;
  initialData?: MuaHangRecord;
  onSubmit?: (data: MuaHangRecord) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
}

const FormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const { entityUnit } = useAuth();
  const { state, actions } = useFormFieldState(initialData || {});
  const [activeTab, setActiveTab] = useState<string>('info');
  const {
    rows: detailRows,
    setRows: setDetailRows,
    selectedRowUuid: detailSelectedRowUuid,
    selectedCell: detailSelectedCell,
    handleRowClick: detailHandleRowClick,
    handleCellClick: detailHandleCellClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange,
    setNonWritableFields: detailSetNonWritableFields,
    setPasteConfigs: detailSetPasteConfigs,
    isPasting: detailIsPasting
  } = useDetailRows(initialData?.chi_tiet_data || [], undefined, handleUpdateRowFields, state.dienGiai);

  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setDetailRows
  });

  useEffect(() => {
    if (!open) {
      setActiveTab('info');
    }
  }, [open]);

  const { so_luong, thanh_tien, thue, tong_tien } = useMemo(() => {
    return calculateTotals(detailRows, state);
  }, [detailRows, state]);

  const handleSubmit = (data: MuaHangRecord) => {
    const formData = transformFormData(data, state, detailRows, entityUnit, so_luong, thanh_tien, thue, tong_tien);
    onSubmit?.(formData);
  };

  const handleClose = () => {
    onClose();
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    handleClose
  });

  if (initialData && formMode === 'add') {
    initialData = {
      ...initialData,
      ngay_ct: new Date().toISOString().split('T')[0],
      ngay_lct: new Date().toISOString().split('T')[0]
    };
  }

  return (
    <>
      <AritoFormV2<FormSchema>
        mode={formMode}
        title={title}
        actionButtons={actionButtons}
        subTitle='Hóa đơn dịch vụ trả lại nhà cung cấp'
        schema={formSchema}
        initialData={initialData || initialFormValues}
        onSubmit={handleSubmit}
        onClose={onClose}
        headerFields={[
          {
            id: 'info',
            label: 'Thông tin',
            component: (
              <div>
                <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
              </div>
            )
          },
          ...(formMode === 'view'
            ? [
                {
                  id: 'history',
                  label: 'Lịch sử',
                  component: <HistoryTab />
                }
              ]
            : [])
        ]}
        headerTabActions={
          formMode !== 'add' && <HoverDropdown uuid={initialData?.uuid} ma_ct={initialData?.chung_tu} />
        }
        tabs={
          activeTab === 'info' && [
            {
              id: 'details',
              label: 'Chi tiết',
              component: (
                <DetailTab
                  formMode={formMode}
                  rows={detailRows}
                  selectedRowUuid={detailSelectedRowUuid}
                  selectedCell={detailSelectedCell}
                  onRowClick={detailHandleRowClick}
                  onCellClick={detailHandleCellClick}
                  onAddRow={detailHandleAddRow}
                  onDeleteRow={detailHandleDeleteRow}
                  onCopyRow={detailHandleCopyRow}
                  onPasteRow={detailHandlePasteRow}
                  onMoveRow={detailHandleMoveRow}
                  onCellValueChange={detailHandleCellValueChange}
                  onRepeatForRows={handleRepeatDetailRows}
                  handleUpdateNonWritableFields={detailSetNonWritableFields}
                  handleUpdatePasteConfigs={detailSetPasteConfigs}
                  isPasting={detailIsPasting}
                />
              )
            },
            {
              id: 'tax',
              label: 'Thuế',
              component: <TaxTab formMode={formMode} formState={{ state, actions }} />
            },
            {
              id: 'e-invoice',
              label: 'HĐĐT(Không sử dụng)',
              component: <EInvoiceTab formMode={formMode} />
            },
            {
              id: 'other',
              label: 'Khác',
              component: <OtherTab formMode={formMode} />
            }
          ]
        }
        bottomBar={<BottomBar so_luong={so_luong} thanh_tien={thanh_tien} thue={thue} tong_tien={tong_tien} />}
      />
    </>
  );
};

export default FormDialog;
